import { IAction } from '@dash/actions';
import { IAgency } from '@dash/agency';
import { getFormattedDate } from 'src/helpers/dateTime';

type TabKey = 'past_agency_show' | 'current_agency_show' | 'future_agency_show';

export const CURRENT_VERSION_KEY: TabKey = 'current_agency_show';
export const PAST_VERSION_KEY: TabKey = 'past_agency_show';
export const FUTURE_VERSION_KEY: TabKey = 'future_agency_show';

export const VERSIONING_ACTIONS = [CURRENT_VERSION_KEY, PAST_VERSION_KEY, FUTURE_VERSION_KEY];

// A tab's start date is date-only, so format without timezone conversion (avoids rolling back a day).
export const formatVersioningTabDate = (startsOn?: string): string =>
  startsOn ? getFormattedDate({ date: startsOn, formatKey: 'date_numeric_long', noLocalTimeZone: true }) : '';

const isVersioningAction = (action: IAction): boolean => VERSIONING_ACTIONS.includes(action.key as TabKey);

// Honour the requested tab only when the loaded contract exposes it, else fall back to Current —
// the redirect can request `future_agency_show` on a contract with no future amendment (e.g. a
// same-day amendment that is now the current contract).
export const resolveInitialTab = (actions: IAction[] | undefined, contractType?: string): TabKey => {
  const availableTabs = (actions || []).filter(isVersioningAction).map(({ key }) => key as TabKey);

  const requestedTab = (contractType || CURRENT_VERSION_KEY) as TabKey;

  if (availableTabs.includes(requestedTab)) return requestedTab;

  if (availableTabs.includes(CURRENT_VERSION_KEY)) return CURRENT_VERSION_KEY;

  return availableTabs[0] || CURRENT_VERSION_KEY;
};

// The action that fetches `contractType` as a separate version; `undefined` when the loaded
// agency doesn't expose it, in which case that agency is edited directly.
export const getVersionAction = (agency: IAgency | null | undefined, contractType?: string): IAction | undefined =>
  agency?.actions.find(action => action.key === contractType);

// The fetched version can omit `legal_entity`; fall back to the base contract's so Step 1 shows it.
export const resolveVersionedAgency = (
  versionedAgency: IAgency | undefined,
  previousAgency: IAgency | null | undefined,
): IAgency =>
  ({
    ...versionedAgency,
    legal_entity: versionedAgency?.legal_entity ?? previousAgency?.legal_entity ?? null,
  } as IAgency);

// The directly-loaded agency when it already is the requested version (or current);
// otherwise the version fetched into `agencyByType`.
export const resolveAgencyToWorkWith = (
  agency: IAgency | null | undefined,
  agencyByType: IAgency | undefined,
  contractType?: string,
): IAgency | null | undefined =>
  contractType === CURRENT_VERSION_KEY || !getVersionAction(agency, contractType) ? agency : agencyByType;
